import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { debounce } from 'debounce';

import AuthLayout from '../components/AuthLayout';
import Button from '../components/inputs/Button';
import TextInput from '../components/inputs/TextInput';
import ThemeLink from '../components/ThemeLink';
import { validateEmail } from '../helpers/validators';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorText from '../components/ErrorText';
import ReactHookFormError from '../components/errors/ReactHookFormError';
import careerProgressImage from '../assets/career-progress-animate.svg';

export default function SignIn() {
  const { signin, resendverificationtoken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const defaultEmail = location?.state?.email;

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    trigger,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: defaultEmail || '',
      password: '',
    },
  });

  const [signingIn, setSigningIn] = useState(false);
  const [signinError, setSigninError] = useState();

  const from = location.state?.from?.pathname || '/dashboard';

  function onSubmit(data) {
    setSigningIn(true);
    signin(data)
      .then(() => navigate(from, { replace: true }))
      .catch((err) => {
        setSigninError(err.message);
      })
      .finally(() => setSigningIn(false));
  }

  function handleClickResendVerification() {
    resendverificationtoken(getValues('email')).catch((err) => {
      setSigninError(err.message);
    });
    setTimeout(() => setSigninError('Verification email sent'), 200);
  }

  const debouncedTriggerValidation = debounce((name) => trigger(name), 500);

  useEffect(() => {
    if (signinError === 'User has not accepted Terms of Use.') {
      navigate('/agree-to-terms', { state: { email: getValues('email') } });
    }
  }, [getValues, navigate, signinError]);

  return (
    <AuthLayout
      heading={
        <div className="h-full flex flex-col">
          <h3>
            Ready to reach your next customer?{' '}
            <span className="text-primary">Let's go!</span>
          </h3>
          <div className="flex flex-grow items-center">
            <img className="w-4/5" src={careerProgressImage} alt="" />
          </div>
        </div>
      }
    >
      {signingIn && <LoadingSpinner message="Signing you in..." />}
      {!signingIn && (
        <form
          className="flex flex-col text-center"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h4>Sign In</h4>
          <Controller
            control={control}
            name="email"
            rules={{
              required: 'Email is required.',
              validate: (v) => validateEmail(v) || `${v} is not a valid email.`,
            }}
            render={({
              field: { ref, onChange, ...restField },
              fieldState: { error, isTouched, isDirty },
            }) => (
              <TextInput
                {...restField}
                ref={ref}
                className="mb-3"
                type="text"
                placeholder="Email"
                error={error}
                validated={Boolean(!error && (isTouched || isDirty))}
                onChange={(value) => {
                  onChange(value);
                  debouncedTriggerValidation('email');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            rules={{
              required: 'Password is required.',
            }}
            render={({
              field: { ref, onChange, ...restField },
              fieldState: { error, isTouched, isDirty },
            }) => (
              <TextInput
                {...restField}
                ref={ref}
                className="mb-3"
                type="password"
                placeholder="Password"
                error={error}
                validated={Boolean(!error && (isTouched || isDirty))}
                onChange={(value) => {
                  onChange(value);
                  debouncedTriggerValidation('password');
                }}
              />
            )}
          />
          <ThemeLink to="/forgotpassword" state={{ email: getValues('email') }}>
            Forgot password?
          </ThemeLink>
          <ReactHookFormError errors={errors} className="mt-3 -mb-9" />
          {signinError && (
            <div>
              <ErrorText message={signinError} className="mt-3 -mb-9" />
              {signinError === 'User is not verified.' && (
                <Button
                  secondary
                  type="button"
                  onClick={handleClickResendVerification}
                >
                  Resend Verification Email
                </Button>
              )}
            </div>
          )}
          <Button className="mt-14 w-80 mx-auto" type="submit" primary>
            Sign In
          </Button>
          <p className="mt-2">
            Don't have an account? <ThemeLink to="/signup">Sign Up</ThemeLink>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
